import Cookies from 'js-cookie';
import { logError } from 'core-framework/util/LogMessage';
import { PointsMaxTrafficType, PriceView } from 'core-framework/constant/Search.types';
import { deserializeCookie, readHybridModeCookies, versionCookieName } from 'core-framework/cookie/HybridModeCookies';
import { AppConfiguration } from 'core/configuration/AppConfiguration';
import { QueryString } from 'core/utils/queryString/queryString';
import { PageTypes } from 'core/constant/analytics/PageTypes';
import { Language } from '@agoda/layout';
import { getBootstrapConfig, getWindowLocation } from 'core/window/windowService';
import { add } from 'date-fns';

const priceViewCookieName = 'agoda.price.01';
const bannerCookieName = 'agoda.banner';
const userCookieName = 'agoda.user.03';
const analyticsCookieName = 'agoda.analytics';
const pointsMaxCookieName = 'agoda.prius';
const appleSignedInCookieName = 'Apple';
const tealiumEnableCookieName = 'tealiumEnable';

const getOneYear = () => add(new Date(), { years: 1 });
const getOneDay = () => add(new Date(), { days: 1 });

const getToplevelDomain = () => {
    const location = getWindowLocation();
    const domain = location.hostname.split('.').slice(-2).join('.'); // TODO: WLSI-398
    return `.${domain}`;
};

export const logErrorMessage = (message: string, error: Error, config = getBootstrapConfig()) => {
    const pageTypeId = config.pageConfig.pageTypeId || PageTypes.Home;
    logError(`${message}, Error: ${error?.message || ''}`, pageTypeId);
};

export const readPriceViewCookie = (suggestedPriceView: number): number => {
    try {
        const cookie = Cookies.get(priceViewCookieName);
        const cookieValue = deserializeCookie(cookie);

        const priceViewString = cookieValue.PriceView;
        if (priceViewString === undefined) {
            return suggestedPriceView;
        }

        const priceView = parseFloat(priceViewString) as PriceView;

        if (priceView !== PriceView.BasePricePerNight && priceView !== PriceView.TotalPricePerNight) {
            throw new Error(`Invalid price view value in cookie - (${priceView})`);
        }

        return priceView;
    } catch (e) {
        logErrorMessage("Can't get PriceView from cookie", e as Error);
        return suggestedPriceView;
    }
};

export const setPriceViewCookie = (priceView: PriceView) => {
    try {
        Cookies.set(priceViewCookieName, `PriceView=${priceView}`, { domain: AppConfiguration.isProduction() ? getToplevelDomain() : undefined, expires: getOneYear() });
    } catch (e) {
        logErrorMessage("Can't write PriceView to cookie", e as Error);
    }
};

export const setBannerCookie = (value: number, pattern: RegExp, banner: string) => {
    try {
        const whiteBannerPattern = pattern;
        const bannerCookie = Cookies.get(bannerCookieName);
        let newCookieValue;
        if (bannerCookie) {
            if (bannerCookie.match(whiteBannerPattern)) {
                newCookieValue = bannerCookie.replace(pattern, `${banner}=${value}`);
            } else {
                newCookieValue = `${bannerCookie}&${banner}=${value}`;
            }
        } else {
            newCookieValue = `${banner}=${value}`;
        }
        Cookies.set(bannerCookieName, newCookieValue, { expires: getOneYear() });
    } catch (e) {
        logErrorMessage(`Can't write banner to cookie, banner: ${banner}`, e as Error);
    }
};

export const setWhiteBannerCookie = (value: number) => {
    const whiteBannerPattern = /WhiteBanner=[0-9a-zA-Z]*|\s&?/gi;
    setBannerCookie(value, whiteBannerPattern, 'WhiteBanner');
};

export const setTallBannerCookie = (value: number) => {
    const whiteBannerPattern = /TallBanner=[0-9a-zA-Z]*|\s&?/gi;
    setBannerCookie(value, whiteBannerPattern, 'TallBanner');
};

export const serializeCookie = (values: Record<string, string>, keyValuePairSeparator = '&', keyValueSeparator = '='): string => {
    return Object.keys(values)
        .map((key) => {
            return `${key}${keyValueSeparator}${values[key]}`;
        })
        .join(keyValuePairSeparator);
};

export const setCurrencyCookie = (currencyCode: string, currencyId = 0) => {
    try {
        if (!currencyCode) {
            return;
        }
        const cookie = Cookies.get(versionCookieName);

        const cookieValues = deserializeCookie(cookie);
        cookieValues.CurLabel = currencyCode;
        cookieValues.CuCur = currencyId.toString();
        const newCookie = serializeCookie(cookieValues);

        Cookies.set(versionCookieName, newCookie, { domain: AppConfiguration.isProduction() ? getToplevelDomain() : undefined, expires: getOneYear() });
    } catch (e) {
        logErrorMessage("Can't write currency to cookie", e as Error);
    }
};

export const setLanguageCookie = (language: Language) => {
    try {
        if (!language) {
            return;
        }
        const cookie = Cookies.get(versionCookieName);

        const cookieValues = deserializeCookie(cookie);
        cookieValues.CulCd = language.cultureCode;
        cookieValues.DLang = language.cultureCode;
        cookieValues.CuLang = language.languageId.toString();
        const newCookie = serializeCookie(cookieValues);

        Cookies.set(versionCookieName, newCookie, { domain: AppConfiguration.isProduction() ? getToplevelDomain() : undefined, expires: getOneYear() });
    } catch (e) {
        logErrorMessage("Can't write language to cookie", e as Error);
    }
};

export const readCurrentCultureCodeCookie = () => {
    const cookie = Cookies.get(versionCookieName);
    const cookieValues = deserializeCookie(cookie);

    return cookieValues.CulCd || null;
};

export const readUserCookie = () => {
    const cookie = Cookies.get(userCookieName);
    return cookie ? cookie.split('&')[0].split('=')[1] : '';
};

export const readOverrideExperimentCookie = () => {
    const values = new QueryString(Cookies.get(userCookieName));
    return {
        expUser: values.get('ExpUser'),
        expList: values.get('ExpList'),
    };
};

export const readAnalyticsCookie = (): {
    id: string | null;
    signature: string | null;
    expiry: string | null;
} => {
    const cookie = Cookies.get(analyticsCookieName);
    const values = deserializeCookie(cookie);

    if (!values || !Object.keys(values).length) {
        return {
            id: null,
            signature: null,
            expiry: null,
        };
    }

    return {
        id: values.Id,
        signature: values.Signature,
        expiry: values.Expiry,
    };
};

export const readPointsMaxCookie = () => {
    const cookie = Cookies.get(pointsMaxCookieName);
    const values = deserializeCookie(cookie);

    return parseInt(values.PriusID, 10) || 0;
};

export const readAppleSignInCookie = () => {
    const cookieVal = Cookies.get(appleSignedInCookieName);
    return (cookieVal && parseInt(cookieVal, 2)) || 0;
};

export const checkAppleSignInCookieExists = () => {
    return Cookies.get(appleSignedInCookieName) !== undefined;
};

export const removeAppleSignInCookie = () => {
    Cookies.remove(appleSignedInCookieName, { domain: 'agoda.com' });
};

export const setPointsMaxCookie = (newPointsMax: number, traffic: PointsMaxTrafficType) => {
    const cookie = Cookies.get(pointsMaxCookieName);
    const values = deserializeCookie(cookie);

    const currentPointsMax = values.PriusID;

    values.PriusID = newPointsMax.toString();
    values.PointsMaxTraffic = traffic;
    values.IsPointsMaxMenuClicked = traffic === PointsMaxTrafficType.Partner ? 'False' : 'True';
    values.HasProgramChanged = 'True';
    if (currentPointsMax) values.lastSelectedPointsmaxProgram = currentPointsMax;

    const newCookie = serializeCookie(values);
    Cookies.set(pointsMaxCookieName, newCookie, { expires: getOneYear(), domain: AppConfiguration.isProduction() ? getToplevelDomain() : undefined });
};

export const setTealiumEnableCookie = (isTealiumEnable: boolean) => {
    Cookies.set(tealiumEnableCookieName, isTealiumEnable.toString(), { expires: getOneDay() });
};

export { readHybridModeCookies, deserializeCookie };
